import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { websiteServicesAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Globe, ArrowRight, ArrowLeft, Check, Loader2, Download, ExternalLink,
  Phone, Mail, Clock, Zap, Shield, Star, FileText, Eye, ChevronDown,
  ChevronUp, Save, AlertCircle, CheckCircle2, Package, Server, Upload,
  RefreshCw, Pencil, MapPin, Palette, MessageSquare, Plus, ImageIcon, X
} from 'lucide-react';
import { toast } from 'sonner';

// ── Constants ──
const STEPS = ['landing', 'questionnaire', 'review', 'package', 'status'];

const QUESTIONNAIRE_SECTIONS = [
  { key: 'business_info', label: '1. Business Information' },
  { key: 'business_type', label: '2. Business Type & Category' },
  { key: 'service_areas', label: '3. Service Areas' },
  { key: 'licensing_trust', label: '4. Licensing & Trust' },
  { key: 'business_details', label: '5. Business Details' },
  { key: 'services_offered', label: '6. Services Offered' },
  { key: 'reviews_reputation', label: '7. Reviews / Reputation' },
  { key: 'faqs', label: '8. FAQs' },
  { key: 'owner_brand', label: '9. Owner / Brand' },
  { key: 'branding_visual', label: '10. Branding & Visual Direction' },
  { key: 'social_links', label: '11. Social Media / Profile Links' },
  { key: 'images_media', label: '12. Images / Media' },
  { key: 'contact_form_email', label: '13. Contact Form / Email Setup' },
  { key: 'structured_data', label: '14. Structured Data / Search' },
  { key: 'final_notes', label: '15. Final Notes' },
];

const FIELD_DEFS = {
  business_info: [
    { key: 'legal_business_name', label: 'Legal Business Name', type: 'text', priority: 'required', placeholder: 'As shown on your Google Business Profile or official records' },
    { key: 'dba_name', label: 'Doing Business As (DBA) Name', type: 'text', priority: 'optional', placeholder: 'If different from your legal business name' },
    { key: 'primary_phone', label: 'Primary Phone Number', type: 'tel', priority: 'required', placeholder: 'The phone number used throughout the website' },
    { key: 'public_email', label: 'Public Contact Email', type: 'email', priority: 'required', placeholder: 'The email shown on the website' },
    { key: 'website_address', label: 'Website Address', type: 'text', priority: 'required', placeholder: 'https://www.yourwebsite.com' },
    { key: 'domain_name', label: 'Domain Name', type: 'text', priority: 'recommended', placeholder: 'yourwebsite.com' },
    { key: 'street_address', label: 'Physical Street Address', type: 'text', priority: 'required', placeholder: 'Provide even if hidden on Google Business Profile' },
    { key: 'city', label: 'City', type: 'text', priority: 'required' },
    { key: 'state', label: 'State', type: 'text', priority: 'required' },
    { key: 'zip_code', label: 'ZIP / Postal Code', type: 'text', priority: 'required' },
    { key: 'country', label: 'Country', type: 'text', priority: 'recommended', placeholder: 'Default: United States' },
    { key: 'address_hidden_on_gbp', label: 'Is your address hidden on Google Business Profile?', type: 'select', options: ['Yes', 'No'], priority: 'required' },
    { key: 'visible_location', label: 'Main visible location on the website', type: 'text', priority: 'optional', placeholder: 'Required if address is hidden. Example: Cape Coral, FL' },
  ],
  business_type: [
    { key: 'business_category', label: 'Business Category', type: 'text', priority: 'required', placeholder: 'Examples: HVAC, Electrical, Plumbing, Roofing' },
    { key: 'residential_commercial', label: 'Residential, Commercial, or Both?', type: 'select', options: ['Residential', 'Commercial', 'Both'], priority: 'required' },
    { key: 'business_type_description', label: 'Most Accurate Business Type', type: 'text', priority: 'required', placeholder: 'Examples: HVAC contractor, electrician, plumber' },
    { key: 'short_business_description', label: 'Short Business Description', type: 'textarea', priority: 'required', placeholder: '2-4 sentences clearly describing your business' },
  ],
  service_areas: [
    { key: 'primary_city', label: 'Primary City', type: 'text', priority: 'required' },
    { key: 'all_cities_served', label: 'All Cities You Actively Serve', type: 'textarea', priority: 'required', placeholder: 'List every city for the website (one per line)' },
    { key: 'counties_served', label: 'Counties You Serve', type: 'textarea', priority: 'recommended' },
    { key: 'states_served', label: 'States You Serve', type: 'text', priority: 'recommended', placeholder: 'If more than one' },
    { key: 'service_area_wording', label: 'Preferred Wording for Service Area', type: 'text', priority: 'optional', placeholder: 'How you want it phrased on the website' },
  ],
  licensing_trust: [
    { key: 'is_licensed', label: 'Are You Licensed?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'license_type', label: 'License Type', type: 'text', priority: 'recommended' },
    { key: 'license_state', label: 'State License Is Held In', type: 'text', priority: 'recommended' },
    { key: 'license_number', label: 'License Number', type: 'text', priority: 'optional', placeholder: 'Only if you want it shown publicly' },
    { key: 'is_insured', label: 'Are You Insured?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'insurance_wording', label: 'Public Insurance Wording', type: 'text', priority: 'recommended', placeholder: 'Example: Licensed & insured' },
    { key: 'year_founded', label: 'Year Founded or Years in Business', type: 'text', priority: 'required' },
    { key: 'certifications', label: 'Certifications, Memberships, Affiliations', type: 'textarea', priority: 'optional', placeholder: 'Examples: NATE, BBB, Chamber of Commerce' },
  ],
  business_details: [
    { key: 'business_hours', label: 'Business Hours', type: 'textarea', priority: 'required', placeholder: 'List days and times' },
    { key: 'emergency_hours', label: 'Emergency / After-Hours Availability', type: 'text', priority: 'optional' },
    { key: 'price_range', label: 'Price Range', type: 'select', options: ['$', '$$', '$$$'], priority: 'recommended' },
    { key: 'payment_methods', label: 'Payment Methods Accepted', type: 'text', priority: 'recommended', placeholder: 'Cash, Check, Credit/Debit, Financing, ACH, etc.' },
    { key: 'main_cta', label: 'Preferred Main Call-to-Action', type: 'text', priority: 'required', placeholder: 'Examples: Call Now, Request an Estimate, Schedule Service' },
    { key: 'secondary_cta', label: 'Secondary Call-to-Action', type: 'text', priority: 'optional' },
  ],
  services_offered: [
    { key: 'services_list', label: 'All Services for the Website', type: 'textarea', priority: 'required', placeholder: 'For each service include:\n- Service name\n- Short description\n- Residential, commercial, or both\n- Related subservices (optional)' },
  ],
  reviews_reputation: [
    { key: 'show_reviews', label: 'Show Reviews on Website?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'review_count', label: 'Total Review Count', type: 'text', priority: 'recommended' },
    { key: 'average_rating', label: 'Average Rating', type: 'text', priority: 'recommended', placeholder: 'e.g. 4.8' },
    { key: 'review_source', label: 'Review Source', type: 'text', priority: 'recommended', placeholder: 'Google, Facebook, Yelp, etc.' },
  ],
  faqs: [
    { key: 'common_questions', label: 'Common Customer Questions', type: 'textarea', priority: 'recommended', placeholder: 'List at least 5 common questions and answers' },
    { key: 'must_answer_questions', label: 'Questions You Definitely Want Answered', type: 'textarea', priority: 'optional' },
  ],
  owner_brand: [
    { key: 'owner_name', label: 'Owner / Founder Name', type: 'text', priority: 'optional' },
    { key: 'owner_title', label: 'Job Title', type: 'text', priority: 'optional', placeholder: 'Owner, Founder, CEO, etc.' },
    { key: 'about_owner', label: 'About the Owner (2-4 sentences)', type: 'textarea', priority: 'optional' },
  ],
  branding_visual: [
    { key: 'headline_style_preference', label: 'Headline Style Preference', type: 'select', options: ['Bold / Modern', 'Elegant / Upscale', 'Clean / Simple', 'Heavy / High-Impact', 'No Preference'], priority: 'recommended' },
    { key: 'website_style_preference', label: 'Overall Website Style', type: 'select', options: ['Modern', 'Luxury', 'Traditional', 'Minimal', 'High-Conversion / Sales-Focused', 'No Preference'], priority: 'recommended' },
    { key: 'has_brand_colors', label: 'Do You Have Brand Colors?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'color_names', label: 'Color Names', type: 'text', priority: 'recommended', placeholder: 'e.g. Navy blue, Gold' },
    { key: 'hex_codes', label: 'Hex Codes', type: 'text', priority: 'optional', placeholder: 'e.g. #1e3a5f, #d4a843' },
    { key: 'style_preferences', label: 'Site Style Keywords', type: 'text', priority: 'recommended', placeholder: 'bold, premium, modern, traditional, luxury, clean, minimal' },
    { key: 'reference_websites', label: 'Competitor or Reference Websites You Like', type: 'textarea', priority: 'optional' },
    { key: 'styles_to_avoid', label: 'Design Styles You Do NOT Want', type: 'textarea', priority: 'optional' },
    { key: 'style_inspiration_screenshots', label: 'Style Inspiration Screenshots', type: 'file_upload', priority: 'optional', placeholder: 'Upload screenshots of websites or designs you like (JPEG, PNG, WebP)' },
  ],
  social_links: [
    { key: 'facebook', label: 'Facebook URL', type: 'text', priority: 'recommended' },
    { key: 'instagram', label: 'Instagram URL', type: 'text', priority: 'recommended' },
    { key: 'linkedin', label: 'LinkedIn URL', type: 'text', priority: 'optional' },
    { key: 'youtube', label: 'YouTube URL', type: 'text', priority: 'optional' },
    { key: 'tiktok', label: 'TikTok URL', type: 'text', priority: 'optional' },
    { key: 'twitter', label: 'X / Twitter URL', type: 'text', priority: 'optional' },
    { key: 'other_socials', label: 'Other Social Profile URLs', type: 'textarea', priority: 'optional' },
    { key: 'google_business_url', label: 'Google Business Profile URL', type: 'text', priority: 'recommended' },
    { key: 'google_maps_url', label: 'Google Maps / Directions URL', type: 'text', priority: 'optional' },
  ],
  images_media: [
    { key: 'has_real_photos', label: 'Do You Have Real Photos?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'photo_notes', label: 'Notes on Where Photos Should Be Used', type: 'textarea', priority: 'optional' },
    { key: 'dark_bg_logo', label: 'Have a Logo Version for Dark Backgrounds?', type: 'select', options: ['Yes', 'No'], priority: 'optional' },
    { key: 'light_bg_logo', label: 'Have a Logo Version for Light Backgrounds?', type: 'select', options: ['Yes', 'No'], priority: 'optional' },
  ],
  contact_form_email: [
    { key: 'recipient_email', label: 'Contact Form Recipient Email', type: 'email', priority: 'required', placeholder: 'Inbox that receives new leads' },
    { key: 'sender_email', label: 'Preferred Sender Email Address', type: 'email', priority: 'recommended', placeholder: 'Email address that sends notifications' },
    { key: 'sender_name', label: 'Preferred Sender Name', type: 'text', priority: 'required', placeholder: 'Usually your business name' },
    { key: 'reply_to_email', label: 'Reply-To Email Address', type: 'email', priority: 'optional' },
    { key: 'autoresponder_phone', label: 'Phone for Autoresponder Email', type: 'tel', priority: 'recommended', placeholder: 'If different from primary phone' },
    { key: 'has_smtp', label: 'Do You Have SMTP Settings Available?', type: 'select', options: ['Yes', 'No'], priority: 'required' },
    { key: 'smtp_host', label: 'SMTP Host', type: 'text', priority: 'optional', placeholder: 'Required if SMTP = Yes' },
    { key: 'smtp_port', label: 'SMTP Port', type: 'text', priority: 'optional', placeholder: 'e.g. 587' },
    { key: 'smtp_encryption', label: 'SMTP Encryption Type', type: 'select', options: ['TLS', 'SSL', 'None'], priority: 'optional' },
    { key: 'smtp_username', label: 'SMTP Username', type: 'text', priority: 'optional' },
    { key: 'smtp_password', label: 'SMTP Password', type: 'text', priority: 'optional' },
    { key: 'email_provider', label: 'Email Provider (if no SMTP)', type: 'text', priority: 'optional', placeholder: 'Google Workspace, Microsoft 365, Zoho, cPanel' },
  ],
  structured_data: [
    { key: 'include_logo_in_search', label: 'Include Logo in Search Markup?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'include_socials_in_search', label: 'Include Social Links in Search Markup?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'include_hours_in_search', label: 'Include Business Hours in Search Markup?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'include_reviews_in_search', label: 'Include Reviews in Search Markup?', type: 'select', options: ['Yes', 'No'], priority: 'recommended' },
    { key: 'lat_lng', label: 'Latitude / Longitude', type: 'text', priority: 'optional' },
    { key: 'search_description_notes', label: 'Special Notes for Search Results', type: 'textarea', priority: 'optional' },
  ],
  final_notes: [
    { key: 'must_include', label: 'Anything You Absolutely Want Included', type: 'textarea', priority: 'optional' },
    { key: 'must_exclude', label: 'Anything You Absolutely Do NOT Want', type: 'textarea', priority: 'optional' },
    { key: 'other_notes', label: 'Anything Else We Should Know', type: 'textarea', priority: 'optional' },
  ],
};

function PriorityBadge({ priority }) {
  if (priority === 'required') return <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">Required</Badge>;
  if (priority === 'recommended') return <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Recommended</Badge>;
  return <Badge className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0">Optional</Badge>;
}


// ── Style Inspiration Upload Component ──
function StyleInspirationUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // value is an array of {path, filename, serve_url}
  const files = Array.isArray(value) ? value : [];

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    const newFiles = [...files];

    for (const file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      try {
        const res = await websiteServicesAPI.uploadInspiration(file);
        newFiles.push({
          path: res.data.path,
          filename: res.data.filename,
          serve_url: res.data.serve_url,
        });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    onChange(newFiles);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = (index) => {
    const updated = files.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-2" data-testid="style-inspiration-uploader">
      <div className="flex flex-wrap gap-2">
        {files.map((f, i) => (
          <div key={f.path || i} className="relative group w-20 h-20 rounded-md overflow-hidden border bg-muted" data-testid={`inspiration-thumb-${i}`}>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${f.serve_url}`}
              alt={f.filename}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <button
              type="button"
              onClick={() => handleRemove(i)}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`remove-inspiration-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-20 h-20 rounded-md border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-1 hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer disabled:opacity-50"
          data-testid="add-inspiration-btn"
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          ) : (
            <>
              <ImageIcon className="w-5 h-5 text-muted-foreground" />
              <span className="text-[9px] text-muted-foreground">Add</span>
            </>
          )}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={handleUpload}
        data-testid="inspiration-file-input"
      />
      <p className="text-[11px] text-muted-foreground">Upload screenshots of websites or designs you like. Max 10MB each.</p>
    </div>
  );
}


// ── Thumbnail Component ──
function BuildThumbnail({ job, onClick }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!job.thumbnail_url) return;
    const token = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${baseUrl}${job.thumbnail_url}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => setImgSrc(URL.createObjectURL(blob)))
      .catch(() => setImgError(true));
  }, [job.thumbnail_url]);

  if (!job.thumbnail_url || imgError) {
    const bizName = job.questionnaire?.business_info?.legal_business_name || 'Website';
    const category = job.questionnaire?.business_type?.business_category || '';
    const colors = job.questionnaire?.branding_visual?.hex_codes || '';
    const primaryColor = colors.split(',')[0]?.trim() || '#1e3a5f';
    return (
      <div
        onClick={onClick}
        className="w-full aspect-video rounded-md overflow-hidden cursor-pointer relative"
        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
        data-testid="build-thumbnail-placeholder"
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-3 text-center">
          <Globe className="w-6 h-6 mb-1.5 opacity-70" />
          <p className="text-xs font-semibold leading-tight truncate w-full">{bizName}</p>
          {category && <p className="text-[10px] opacity-60 truncate w-full">{category}</p>}
        </div>
      </div>
    );
  }

  if (!imgSrc) {
    return (
      <div className="w-full aspect-video rounded-md bg-muted animate-pulse" />
    );
  }

  return (
    <img
      src={imgSrc}
      alt="Website preview"
      onClick={onClick}
      className="w-full aspect-video rounded-md object-cover object-top cursor-pointer hover:opacity-90 transition-opacity"
      data-testid="build-thumbnail-image"
    />
  );
}


// ── Landing Section ──
function LandingSection({ onStart, existingJobs, onResumeJob, onRequestRevision }) {
  const activeJob = existingJobs.find(j => !['completed', 'failed'].includes(j.status));
  const completedJobs = existingJobs.filter(j => j.status === 'completed');

  return (
    <div className="space-y-10" data-testid="website-landing">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-8 sm:p-12 text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #3b82f6 0%, transparent 50%), radial-gradient(circle at 80% 20%, #06b6d4 0%, transparent 50%)' }} />
        <div className="relative max-w-2xl">
          <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 mb-4">New Service</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Get a Professional Website Built for Your Business</h1>
          <p className="text-base sm:text-lg text-blue-100/80 mb-6 leading-relaxed">
            A custom, conversion-focused website — built from your answers in minutes. No design skills needed.
          </p>
          <div className="flex flex-wrap gap-3">
            {activeJob ? (
              <Button size="lg" onClick={() => onResumeJob(activeJob)} className="bg-white text-slate-900 hover:bg-blue-50" data-testid="resume-build-btn">
                Continue Your Build <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button size="lg" onClick={onStart} className="bg-white text-slate-900 hover:bg-blue-50" data-testid="start-build-btn">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Completed builds */}
      {completedJobs.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Your Website Builds</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {completedJobs.map(job => {
              const bizName = job.questionnaire?.business_info?.legal_business_name || 'Website Build';
              const pkg = job.package_type === 'self_hosted' ? 'Self-Hosted' : job.package_type?.startsWith('hosted') ? 'Hosted' : null;
              const completedDate = job.completed_at ? new Date(job.completed_at) : null;
              const previewUrl = job.preview_url ? `${process.env.REACT_APP_BACKEND_URL}${job.preview_url}` : null;
              return (
                <Card key={job.id} className="overflow-hidden" data-testid="completed-build-card">
                  {/* Thumbnail */}
                  <div className="px-3 pt-3">
                    <BuildThumbnail
                      job={job}
                      onClick={() => previewUrl && window.open(previewUrl, '_blank')}
                    />
                  </div>
                  <div className="p-3 space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold truncate leading-tight text-sm">{bizName}</p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">Completed</Badge>
                          {pkg && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{pkg}</Badge>}
                          {job.provider === 'claude' && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">AI Built</Badge>}
                        </div>
                      </div>
                    </div>
                    {completedDate && (
                      <p className="text-[11px] text-muted-foreground">
                        {completedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {completedDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {previewUrl && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open(previewUrl, '_blank')} data-testid="preview-btn">
                          <Eye className="w-3 h-3 mr-1" /> Preview
                        </Button>
                      )}
                      <Button size="sm" className="h-7 text-xs" onClick={() => onResumeJob(job)} data-testid="download-build-btn">
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => onRequestRevision(job)} data-testid="request-revision-btn">
                        <RefreshCw className="w-3 h-3 mr-1" /> Revise
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* How it works */}
      <div>
        <h2 className="text-lg font-semibold mb-4">How It Works</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: FileText, title: '1. Fill Out a Questionnaire', desc: 'Tell us about your business, services, and branding.' },
            { icon: Zap, title: '2. We Build Your Site', desc: 'Your custom website is generated in 15–20 minutes.' },
            { icon: Download, title: '3. Download & Launch', desc: 'Get a ZIP package ready to upload to any hosting provider.' },
          ].map((item, i) => (
            <Card key={i} className="p-5">
              <item.icon className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="font-semibold mb-1">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* What's included */}
      <div>
        <h2 className="text-lg font-semibold mb-4">What's Included</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            'Custom Home page with hero section',
            'Services page with your offerings',
            'About page with your company story',
            'Contact page with working form',
            'Mobile-responsive design',
            'SEO-friendly structure',
            'Contact form email handler',
            'Setup instructions (README)',
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm">
              <Check className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Screenshots placeholder */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Example Website Builds</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { name: 'HVAC Pro Website', color: 'from-blue-600 to-blue-800', image: null },
            { name: 'Appliance Repair Website', color: null, image: '/marketing-examples/appliance-repair-hero.jpg' },
            { name: 'Electrical Services', color: 'from-amber-600 to-amber-800', image: null },
          ].map((ex, i) => (
            ex.image ? (
              <div
                key={i}
                className="rounded-xl overflow-hidden aspect-video relative shadow-sm ring-1 ring-black/5"
                data-testid={`example-build-${i}`}
              >
                <img
                  src={ex.image}
                  alt={ex.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Right-side dark gradient — keeps the label legible on the
                    right edge while leaving the hero headline (left/center
                    of the screenshot) untouched. */}
                <div className="absolute inset-0 bg-gradient-to-l from-black/85 via-black/30 to-transparent" />
                <div className="absolute inset-0 p-6 flex items-end justify-end text-right text-white">
                  <div className="max-w-[55%]">
                    <p className="font-semibold text-sm drop-shadow">{ex.name}</p>
                    <p className="text-xs opacity-80">Example build</p>
                  </div>
                </div>
              </div>
            ) : (
              <div
                key={i}
                className={`rounded-xl bg-gradient-to-br ${ex.color} p-6 text-white aspect-video flex items-end justify-end text-right`}
                data-testid={`example-build-${i}`}
              >
                <div>
                  <p className="font-semibold text-sm">{ex.name}</p>
                  <p className="text-xs opacity-70">Example build</p>
                </div>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Pricing overview */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Pricing</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Card className="p-6 border-2 border-blue-200 bg-blue-50/50">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold">Self-Hosted</h3>
            </div>
            <p className="text-3xl font-bold mb-1">$499</p>
            <p className="text-sm text-muted-foreground mb-4">One-time payment</p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0" /> Full website ZIP package</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0" /> Upload to your own hosting</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0" /> Setup instructions included</li>
            </ul>
          </Card>
          <Card className="p-6 border-2 border-emerald-200 bg-emerald-50/50">
            <div className="flex items-center gap-2 mb-3">
              <Server className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold">Hosted by Stock & Load</h3>
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Recommended</Badge>
            </div>
            <p className="text-3xl font-bold mb-1">$599 <span className="text-base font-normal text-muted-foreground">+ hosting</span></p>
            <p className="text-sm text-muted-foreground mb-4">$24.99/mo or $250/year hosting <span className="text-emerald-700 font-medium">— save with yearly</span></p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> Everything in Self-Hosted</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> We host and manage for you</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> No technical setup required</li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Notes */}
      <Card className="p-5 bg-amber-50/50 border-amber-200">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-medium text-amber-800">Important Notes</p>
            <p className="text-amber-700">Self-hosted packages require you to configure contact form email settings. Instructions are included in the ZIP.</p>
            <p className="text-amber-700">Delivery time: approximately 3–8 minutes after purchase.</p>
          </div>
        </div>
      </Card>

      {/* Support */}
      <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> support@stockandload.com</div>
        <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> (239) 446-8230</div>
      </div>

      {/* Bottom CTA */}
      <div className="text-center pb-4">
        <Button size="lg" onClick={activeJob ? () => onResumeJob(activeJob) : onStart} data-testid="bottom-cta-btn">
          {activeJob ? 'Continue Your Build' : 'Start Your Website Build'} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}


// ── Questionnaire Section ──
function QuestionnaireSection({ data, onChange, onBack, onNext, onSave, saving }) {
  const [openSection, setOpenSection] = useState('business_info');

  const handleFieldChange = (section, field, value) => {
    const updated = { ...data, [section]: { ...(data[section] || {}), [field]: value } };
    onChange(updated);
  };

  const requiredFilled = () => {
    const biz = data.business_info || {};
    return !!(biz.legal_business_name && biz.primary_phone && biz.public_email && biz.website_address && biz.city && biz.state && biz.zip_code);
  };
  const contactFilled = () => {
    const c = data.contact_form_email || {};
    return !!c.recipient_email;
  };

  return (
    <div className="space-y-4" data-testid="website-questionnaire">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold">Website Questionnaire</h2>
          <p className="text-sm text-muted-foreground">Fill in your business details. Fields are marked as required, recommended, or optional.</p>
        </div>
        <Button variant="outline" size="sm" onClick={onSave} disabled={saving} data-testid="save-draft-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          Save Draft
        </Button>
      </div>

      {QUESTIONNAIRE_SECTIONS.map(section => {
        const isOpen = openSection === section.key;
        const fields = FIELD_DEFS[section.key] || [];
        const sectionData = data[section.key] || {};
        const filledCount = fields.filter(f => {
          const val = sectionData[f.key];
          return Array.isArray(val) ? val.length > 0 : !!val;
        }).length;

        return (
          <Card key={section.key} className="overflow-hidden">
            <button
              onClick={() => setOpenSection(isOpen ? null : section.key)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
              data-testid={`section-${section.key}`}
            >
              <div className="flex items-center gap-3">
                <span className="font-medium">{section.label}</span>
                {filledCount > 0 && <Badge variant="secondary" className="text-xs">{filledCount}/{fields.length}</Badge>}
              </div>
              {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {isOpen && (
              <div className="px-4 pb-5 space-y-4 border-t">
                {fields.map(field => (
                  <div key={field.key} className="pt-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Label className="text-sm font-medium">{field.label}</Label>
                      <PriorityBadge priority={field.priority} />
                    </div>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={sectionData[field.key] || ''}
                        onChange={e => handleFieldChange(section.key, field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        rows={3}
                        className="text-sm"
                        data-testid={`field-${field.key}`}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        value={sectionData[field.key] || ''}
                        onChange={e => handleFieldChange(section.key, field.key, e.target.value)}
                        className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
                        data-testid={`field-${field.key}`}
                      >
                        <option value="">Select...</option>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : field.type === 'file_upload' ? (
                      <StyleInspirationUploader
                        value={sectionData[field.key] || []}
                        onChange={(files) => handleFieldChange(section.key, field.key, files)}
                      />
                    ) : (
                      <Input
                        type={field.type === 'url' ? 'text' : field.type}
                        value={sectionData[field.key] || ''}
                        onChange={e => handleFieldChange(section.key, field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        className="text-sm"
                        data-testid={`field-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}

      <div className="flex items-center justify-between pt-4 gap-3 flex-wrap">
        <Button variant="outline" onClick={onBack} data-testid="questionnaire-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <div className="flex items-center gap-2">
          {(!requiredFilled() || !contactFilled()) && (
            <span className="text-xs text-amber-600">Fill required fields to continue</span>
          )}
          <Button onClick={onNext} disabled={!requiredFilled() || !contactFilled()} data-testid="questionnaire-next-btn">
            Review & Continue <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── Review Section ──
function ReviewSection({ data, onBack, onNext }) {
  // Calculate completion stats
  const sectionStats = QUESTIONNAIRE_SECTIONS.map(section => {
    const fields = FIELD_DEFS[section.key] || [];
    const sectionData = data[section.key] || {};
    const isFilled = (f) => {
      const val = sectionData[f.key];
      if (Array.isArray(val)) return val.length > 0;
      return !!val;
    };
    const filled = fields.filter(isFilled).length;
    const required = fields.filter(f => f.priority === 'required');
    const requiredFilled = required.filter(isFilled).length;
    return { ...section, fields, filled, total: fields.length, requiredFilled, requiredTotal: required.length };
  });

  const totalFilled = sectionStats.reduce((s, x) => s + x.filled, 0);
  const totalFields = sectionStats.reduce((s, x) => s + x.total, 0);
  const allRequiredFilled = sectionStats.every(s => s.requiredFilled === s.requiredTotal);

  return (
    <div className="space-y-5" data-testid="website-review">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold">Review Your Answers</h2>
          <p className="text-sm text-muted-foreground">Double-check your responses before choosing a package.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-sm font-semibold">{totalFilled} / {totalFields} fields</p>
            <p className="text-[11px] text-muted-foreground">{allRequiredFilled ? 'All required fields complete' : 'Some required fields missing'}</p>
          </div>
          <div className="relative w-10 h-10">
            <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3" className="text-muted/20" />
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="3" className="text-blue-600"
                strokeDasharray={`${(totalFilled / totalFields) * 97.4} 97.4`} strokeLinecap="round" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">{Math.round((totalFilled / totalFields) * 100)}%</span>
          </div>
        </div>
      </div>

      {sectionStats.map(section => {
        const sectionData = data[section.key] || {};
        const filledEntries = section.fields.filter(f => sectionData[f.key]);
        const emptyFields = section.fields.filter(f => !sectionData[f.key]);
        const hasData = filledEntries.length > 0;

        return (
          <Card key={section.key} className={`overflow-hidden ${!hasData ? 'opacity-60' : ''}`} data-testid={`review-section-${section.key}`}>
            <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b">
              <div className="flex items-center gap-2.5">
                {section.filled === section.total ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                ) : hasData ? (
                  <div className="w-4 h-4 rounded-full border-2 border-blue-500 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  </div>
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                )}
                <h3 className="font-semibold text-sm">{section.label}</h3>
              </div>
              <Badge variant="secondary" className="text-[10px] shrink-0">
                {section.filled}/{section.total}
              </Badge>
            </div>

            {hasData ? (
              <div className="px-4 py-3 space-y-2">
                {filledEntries.map(field => (
                  <div key={field.key} className="grid grid-cols-[minmax(140px,200px)_1fr] gap-x-3 gap-y-0.5 text-sm" data-testid={`review-field-${field.key}`}>
                    <span className="text-muted-foreground flex items-start gap-1.5">
                      <span className="truncate">{field.label}</span>
                      {field.priority === 'required' && <span className="text-red-400 text-[10px] mt-0.5">*</span>}
                    </span>
                    <span className="break-words whitespace-pre-wrap text-foreground">
                      {field.type === 'file_upload' && Array.isArray(sectionData[field.key])
                        ? `${sectionData[field.key].length} image(s) uploaded`
                        : String(sectionData[field.key])}
                    </span>
                  </div>
                ))}
                {emptyFields.length > 0 && (
                  <p className="text-[11px] text-muted-foreground pt-1 border-t mt-2">
                    {emptyFields.length} field{emptyFields.length > 1 ? 's' : ''} skipped: {emptyFields.map(f => f.label).join(', ')}
                  </p>
                )}
              </div>
            ) : (
              <div className="px-4 py-4 text-sm text-muted-foreground italic">
                No answers provided for this section.
              </div>
            )}
          </Card>
        );
      })}

      <div className="flex items-center justify-between pt-4 gap-3">
        <Button variant="outline" onClick={onBack} data-testid="review-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Edit Answers
        </Button>
        <Button onClick={onNext} data-testid="review-next-btn">
          Choose Package <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}


// ── Package Selection Section ──
function PackageSection({ onBack, onSelectPackage, loading }) {
  const [selected, setSelected] = useState(null);
  const [hostingInterval, setHostingInterval] = useState('monthly');

  const handlePurchase = () => {
    if (!selected) return;
    const type = selected === 'self_hosted' ? 'self_hosted' : `hosted_${hostingInterval}`;
    onSelectPackage(type);
  };

  return (
    <div className="space-y-6" data-testid="website-package-selection">
      <div>
        <h2 className="text-xl font-bold">Choose Your Package</h2>
        <p className="text-sm text-muted-foreground">Select how you'd like your website delivered.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Self-Hosted */}
        <Card
          className={`p-5 cursor-pointer transition-all ${selected === 'self_hosted' ? 'ring-2 ring-blue-500 border-blue-300' : 'hover:border-blue-200'}`}
          onClick={() => setSelected('self_hosted')}
          data-testid="package-self-hosted"
        >
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold">Self-Hosted Package</h3>
          </div>
          <p className="text-3xl font-bold text-blue-700 mb-2">$499</p>
          <p className="text-sm text-muted-foreground mb-4">One-time payment</p>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0" /> Complete website ZIP download</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0" /> Upload to GoDaddy, SiteGround, etc.</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0" /> Full ownership of all files</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-blue-600 shrink-0" /> Setup instructions included</li>
          </ul>
          <p className="text-xs text-amber-600 mt-3">Contact form email settings must be configured manually.</p>
          {selected === 'self_hosted' && <div className="mt-3 flex items-center gap-1.5 text-sm font-medium text-blue-600"><CheckCircle2 className="w-4 h-4" /> Selected</div>}
        </Card>

        {/* Hosted */}
        <Card
          className={`p-5 cursor-pointer transition-all ${selected === 'hosted' ? 'ring-2 ring-emerald-500 border-emerald-300' : 'hover:border-emerald-200'}`}
          onClick={() => setSelected('hosted')}
          data-testid="package-hosted"
        >
          <div className="flex items-center gap-2 mb-3">
            <Server className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold">Hosted by Stock & Load</h3>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Recommended</Badge>
          </div>
          <p className="text-3xl font-bold text-emerald-700 mb-1">$599</p>
          <p className="text-sm text-muted-foreground mb-4">One-time build fee + hosting below</p>

          {selected === 'hosted' && (
            <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200" onClick={e => e.stopPropagation()}>
              <p className="text-xs font-medium text-emerald-800 mb-2">Choose hosting plan:</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  className={`p-2.5 rounded-lg border text-center text-sm transition-all ${hostingInterval === 'monthly' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-emerald-300 hover:bg-emerald-50'}`}
                  onClick={() => setHostingInterval('monthly')}
                  data-testid="hosting-monthly-btn"
                >
                  <span className="font-semibold block">$24.99/mo</span>
                  <span className="text-[10px] opacity-80">Monthly</span>
                </button>
                <button
                  className={`p-2.5 rounded-lg border text-center text-sm transition-all ${hostingInterval === 'yearly' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-emerald-300 hover:bg-emerald-50'}`}
                  onClick={() => setHostingInterval('yearly')}
                  data-testid="hosting-yearly-btn"
                >
                  <span className="font-semibold block">$250/year</span>
                  <span className="text-[10px] opacity-80">Save $50/yr</span>
                </button>
              </div>
            </div>
          )}

          <ul className="space-y-2 text-sm">
            <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> Everything in Self-Hosted</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> We host and manage your site</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> No technical setup required</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> Contact form works out of the box</li>
          </ul>
          {selected === 'hosted' && <div className="mt-3 flex items-center gap-1.5 text-sm font-medium text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Selected</div>}
        </Card>
      </div>

      <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
        <Button variant="outline" onClick={onBack} data-testid="package-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <Button onClick={handlePurchase} disabled={!selected || loading} data-testid="purchase-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
          {selected === 'self_hosted' ? 'Purchase — $499' : selected === 'hosted' ? `Purchase — $599 + ${hostingInterval === 'monthly' ? '$24.99/mo' : '$250/year'}` : 'Select a Package'}
        </Button>
      </div>
    </div>
  );
}


// ── Status/Completion Section ──
function StatusSection({ job, onBack, onPoll, onRequestRevision }) {
  const status = job?.status;
  const isBuilding = ['queued', 'building', 'purchased'].includes(status);
  const isComplete = status === 'completed';
  const isFailed = status === 'failed';

  useEffect(() => {
    if (!isBuilding) return;
    const interval = setInterval(onPoll, 3000);
    return () => clearInterval(interval);
  }, [isBuilding, onPoll]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${baseUrl}/api/website-services/jobs/${job.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const biz = job.questionnaire?.business_info?.legal_business_name || 'website';
      a.download = `${biz.replace(/[^a-zA-Z0-9 -]/g, '').replace(/ /g, '-')}-website.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download. Please try again.');
    }
  };

  return (
    <div className="space-y-6" data-testid="website-status">
      {/* Building state */}
      {isBuilding && (
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Building Your Website</h2>
          <p className="text-muted-foreground mb-1">This usually takes 3–8 minutes. You can leave this page and come back.</p>
          <Badge className="mt-2">{status === 'queued' ? 'Queued' : 'Building...'}</Badge>
        </Card>
      )}

      {/* Complete state */}
      {isComplete && (
        <>
          <Card className="p-6 sm:p-8 border-2 border-green-200 bg-green-50/30">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-10 h-10 text-green-600 shrink-0" />
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-green-800 mb-1">Your Website Build is Complete!</h2>
                <p className="text-sm text-green-700 mb-1">
                  {job.questionnaire?.business_info?.legal_business_name || 'Your website'} is ready.
                </p>
                <p className="text-xs text-muted-foreground">
                  Completed: {new Date(job.completed_at).toLocaleString()} | Package: {job.package_type === 'self_hosted' ? 'Self-Hosted' : 'Hosted'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-5">
              <Button onClick={handleDownload} data-testid="download-zip-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download ZIP
              </Button>
              {job.preview_url && (
                <Button variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${job.preview_url}`, '_blank')} data-testid="preview-site-btn">
                  <Eye className="w-4 h-4 mr-1.5" /> Preview Site
                </Button>
              )}
              <Button variant="outline" onClick={() => onRequestRevision && onRequestRevision(job)} data-testid="status-request-revision-btn">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Request Revision
              </Button>
            </div>
          </Card>

          {/* Self-hosted instructions */}
          {job.package_type === 'self_hosted' && (
            <Card className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Upload className="w-4 h-4" /> Self-Hosted Setup</h3>
              <ol className="space-y-2 text-sm list-decimal list-inside text-muted-foreground">
                <li>Unzip the downloaded file on your computer.</li>
                <li>Log in to your hosting provider (GoDaddy, SiteGround, etc.).</li>
                <li>Upload all files to the <code className="bg-muted px-1 rounded text-foreground">public_html</code> or <code className="bg-muted px-1 rounded text-foreground">www</code> directory.</li>
                <li><strong className="text-foreground">Important:</strong> Open <code className="bg-muted px-1 rounded text-foreground">contact-handler.php</code> and set your email address for the contact form to work.</li>
                <li>Visit your domain to verify everything looks correct.</li>
              </ol>
            </Card>
          )}

          {/* Hosting upsell for self-hosted */}
          {job.package_type === 'self_hosted' && (
            <Card className="p-5 border-emerald-200 bg-emerald-50/30">
              <div className="flex items-start gap-3">
                <Server className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-emerald-800 mb-1">Don't want to host it yourself?</h3>
                  <p className="text-sm text-emerald-700 mb-2">
                    Let Stock & Load host your website. We handle setup, hosting, and maintenance — starting at $24.99/month or $250/year.
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> support@stockandload.com</span>
                    <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> (239) 446-8230</span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Support box */}
          <Card className="p-5">
            <h3 className="font-semibold mb-2 flex items-center gap-2"><Shield className="w-4 h-4" /> Need Help?</h3>
            <p className="text-sm text-muted-foreground mb-3">Our team is here to help with setup, customization, or any questions.</p>
            <div className="flex flex-wrap gap-4 text-sm">
              <a href="mailto:support@stockandload.com" className="flex items-center gap-1.5 text-blue-600 hover:underline"><Mail className="w-4 h-4" /> support@stockandload.com</a>
              <a href="tel:2394468230" className="flex items-center gap-1.5 text-blue-600 hover:underline"><Phone className="w-4 h-4" /> (239) 446-8230</a>
            </div>
          </Card>
        </>
      )}

      {/* Failed state */}
      {isFailed && (
        <Card className="p-6 border-red-200 bg-red-50/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-8 h-8 text-red-600 shrink-0" />
            <div>
              <h2 className="text-lg font-bold text-red-800 mb-1">Build Failed</h2>
              <p className="text-sm text-red-700 mb-2">{job.error_message || 'Something went wrong during the build.'}</p>
              <p className="text-sm text-muted-foreground">Please contact support:</p>
              <div className="flex flex-wrap gap-4 text-sm mt-1">
                <span><Mail className="w-3.5 h-3.5 inline mr-1" />support@stockandload.com</span>
                <span><Phone className="w-3.5 h-3.5 inline mr-1" />(239) 446-8230</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {(isComplete || isFailed) && (
        <Button variant="outline" onClick={onBack} data-testid="back-to-landing-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Website Services
        </Button>
      )}
    </div>
  );
}


// ── Revision Section ──
const REVISION_TYPES = [
  { key: 'update_content', label: 'Update Content / Text', desc: 'Change text, descriptions, or information on specific pages', icon: Pencil },
  { key: 'add_service_page', label: 'Add Service Page', desc: 'Add a new service page to your website', icon: Plus },
  { key: 'add_city_page', label: 'Add City / Area Page', desc: 'Add a new city or service area page', icon: MapPin },
  { key: 'update_branding', label: 'Update Branding / Colors', desc: 'Change colors, fonts, or visual styling', icon: Palette },
  { key: 'custom', label: 'Custom Request', desc: 'Any other changes or modifications', icon: MessageSquare },
];

function RevisionSection({ job, onBack, onComplete }) {
  const [revisionType, setRevisionType] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [affectedPages, setAffectedPages] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeRevision, setActiveRevision] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [step, setStep] = useState('type'); // type, details, processing

  // Load existing revisions and detect active ones
  useEffect(() => {
    if (job?.id) {
      websiteServicesAPI.listRevisions(job.id).then(r => {
        setRevisions(r.data);
        // If there's an active (queued/building) revision, show it
        const active = r.data.find(rev => ['queued', 'building'].includes(rev.status));
        if (active) {
          setActiveRevision(active);
          setStep('processing');
        }
      }).catch(() => {});
    }
  }, [job?.id]);

  // Poll active revision
  useEffect(() => {
    if (!activeRevision || !['queued', 'building'].includes(activeRevision.status)) return;
    const interval = setInterval(async () => {
      try {
        const res = await websiteServicesAPI.getRevision(activeRevision.id);
        setActiveRevision(res.data);
        if (res.data.status === 'completed') {
          toast.success('Revision complete! Your updated website is ready.');
          websiteServicesAPI.listRevisions(job.id).then(r => setRevisions(r.data)).catch(() => {});
        } else if (res.data.status === 'failed') {
          toast.error('Revision failed. Please try again or contact support.');
        }
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeRevision?.id, activeRevision?.status, job?.id]);

  const handleSubmit = async () => {
    if (!revisionType || !instructions.trim()) return;
    setSubmitting(true);
    try {
      const pages = affectedPages.split('\n').map(p => p.trim()).filter(Boolean);
      // Step 1: Create revision (draft)
      const res = await websiteServicesAPI.createRevision(job.id, {
        revision_type: revisionType,
        instructions: instructions.trim(),
        affected_pages: pages.length > 0 ? pages : null,
      });
      // Step 2: Create Stripe checkout session (or comped)
      const checkoutRes = await websiteServicesAPI.revisionCheckout(res.data.id);
      // Handle comped tenant — revision starts immediately
      if (checkoutRes.data.comped) {
        setActiveRevision(checkoutRes.data.revision);
        setStep('processing');
        toast.success('Revision started! Your account has billing bypass enabled.');
        setSubmitting(false);
        return;
      }
      // Step 3: Redirect to Stripe
      window.location.href = checkoutRes.data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to create checkout. Please try again.';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const handleDownloadRevision = async (rev) => {
    try {
      const token = localStorage.getItem('access_token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${baseUrl}/api/website-services/revisions/${rev.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const biz = job.questionnaire?.business_info?.legal_business_name || 'website';
      a.download = `${biz.replace(/[^a-zA-Z0-9 -]/g, '').replace(/ /g, '-')}-revised.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download revision.');
    }
  };

  // Processing state
  if (step === 'processing' && activeRevision) {
    const isBuilding = ['queued', 'building'].includes(activeRevision.status);
    const isComplete = activeRevision.status === 'completed';
    const isFailed = activeRevision.status === 'failed';

    return (
      <div className="space-y-5" data-testid="revision-processing">
        {isBuilding && (
          <Card className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Processing Your Revision</h2>
            <p className="text-muted-foreground mb-1">This usually takes 1–3 minutes for targeted revisions.</p>
            <Badge className="mt-2">{activeRevision.status === 'queued' ? 'Queued' : 'Building...'}</Badge>
          </Card>
        )}

        {isComplete && (
          <Card className="p-6 border-2 border-green-200 bg-green-50/30">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-10 h-10 text-green-600 shrink-0" />
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-green-800 mb-1">Revision Complete!</h2>
                <p className="text-sm text-green-700 mb-2">
                  Your website has been updated. {activeRevision.files_changed?.length || 0} file(s) changed.
                </p>
                {activeRevision.files_changed && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Files updated: {activeRevision.files_changed.join(', ')}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <Button onClick={() => handleDownloadRevision(activeRevision)} data-testid="download-revision-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download Updated ZIP
              </Button>
              <Button variant="outline" onClick={() => { setStep('type'); setActiveRevision(null); setRevisionType(null); setInstructions(''); }} data-testid="new-revision-btn">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Another Revision
              </Button>
            </div>
          </Card>
        )}

        {isFailed && (
          <Card className="p-6 border-red-200 bg-red-50/30">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-8 h-8 text-red-600 shrink-0" />
              <div>
                <h2 className="text-lg font-bold text-red-800 mb-1">Revision Failed</h2>
                <p className="text-sm text-red-700 mb-2">{activeRevision.error_message || 'Something went wrong.'}</p>
              </div>
            </div>
            <Button variant="outline" className="mt-3" onClick={() => { setStep('type'); setActiveRevision(null); }}>
              Try Again
            </Button>
          </Card>
        )}

        <Button variant="outline" onClick={onBack} data-testid="revision-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Website Services
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="revision-section">
      <div>
        <h2 className="text-xl font-bold">Request a Revision</h2>
        <p className="text-sm text-muted-foreground">
          Make targeted changes to your website — only the affected pages will be regenerated.
        </p>
      </div>

      {step === 'type' && (
        <>
          <Card className="flex items-center gap-3 p-3 bg-blue-50/50 border-blue-100" data-testid="revision-pricing-note">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <Zap className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium">$99 per revision</p>
              <p className="text-xs text-muted-foreground">Any type of change — content, pages, branding, or custom updates.</p>
            </div>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2">
            {REVISION_TYPES.map(type => (
              <Card
                key={type.key}
                className={`p-4 cursor-pointer transition-all ${revisionType === type.key ? 'ring-2 ring-blue-500 border-blue-300' : 'hover:border-blue-200'}`}
                onClick={() => setRevisionType(type.key)}
                data-testid={`revision-type-${type.key}`}
              >
                <div className="flex items-start gap-3">
                  <type.icon className={`w-5 h-5 shrink-0 mt-0.5 ${revisionType === type.key ? 'text-blue-600' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{type.desc}</p>
                  </div>
                </div>
                {revisionType === type.key && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Selected
                  </div>
                )}
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 gap-3">
            <Button variant="outline" onClick={onBack} data-testid="revision-cancel-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            <Button onClick={() => setStep('details')} disabled={!revisionType} data-testid="revision-next-btn">
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </>
      )}

      {step === 'details' && (
        <>
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {(() => { const T = REVISION_TYPES.find(t => t.key === revisionType); return T ? <T.icon className="w-5 h-5 text-blue-600" /> : null; })()}
                <h3 className="font-semibold">{REVISION_TYPES.find(t => t.key === revisionType)?.label}</h3>
              </div>
              <Badge variant="outline" className="text-sm font-semibold px-2.5 py-0.5" data-testid="revision-price-badge">$99</Badge>
            </div>

            <div>
              <Label className="text-sm font-medium">What changes do you need?</Label>
              <Textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                placeholder={
                  revisionType === 'add_service_page' ? 'Describe the new service to add (name, description, subservices, etc.)' :
                  revisionType === 'add_city_page' ? 'Enter the city name and any specific details about services offered there' :
                  revisionType === 'update_branding' ? 'Describe the branding changes (new colors, fonts, style preferences)' :
                  revisionType === 'update_content' ? 'Describe what text/content should change and on which pages' :
                  'Describe the changes you need...'
                }
                rows={4}
                className="mt-1.5"
                data-testid="revision-instructions"
              />
            </div>

            {(revisionType === 'update_content' || revisionType === 'custom') && (
              <div>
                <Label className="text-sm font-medium">Specific pages to update (optional)</Label>
                <Textarea
                  value={affectedPages}
                  onChange={e => setAffectedPages(e.target.value)}
                  placeholder="One page path per line, e.g.:\nindex.html\nabout/index.html\nservices/hvac-repair/index.html"
                  rows={3}
                  className="mt-1.5 text-xs font-mono"
                  data-testid="revision-affected-pages"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Leave empty to let us determine which pages need updating.</p>
              </div>
            )}
          </Card>

          <div className="flex items-center justify-between pt-2 gap-3">
            <Button variant="outline" onClick={() => setStep('type')} data-testid="revision-details-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            <Button onClick={handleSubmit} disabled={!instructions.trim() || submitting} data-testid="revision-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Pay $99 & Submit Revision
            </Button>
          </div>
        </>
      )}

      {/* Revision History */}
      {revisions.length > 0 && step === 'type' && (
        <div className="pt-4">
          <h3 className="font-semibold mb-3">Revision History</h3>
          <div className="space-y-2">
            {revisions.map(rev => (
              <Card key={rev.id} className="p-3 flex items-center justify-between gap-3" data-testid="revision-history-item">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{REVISION_TYPES.find(t => t.key === rev.revision_type)?.label || rev.revision_type}</p>
                    <Badge className={`text-[10px] px-1.5 py-0 ${
                      rev.status === 'completed' ? 'bg-green-100 text-green-700' :
                      rev.status === 'failed' ? 'bg-red-100 text-red-700' :
                      rev.status === 'building' || rev.status === 'queued' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {rev.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{rev.instructions}</p>
                  {rev.created_at && <p className="text-[10px] text-muted-foreground">{new Date(rev.created_at).toLocaleString()}</p>}
                </div>
                {rev.status === 'completed' && (
                  <Button size="sm" className="h-7 text-xs shrink-0" onClick={() => handleDownloadRevision(rev)}>
                    <Download className="w-3 h-3 mr-1" /> Download
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Main Page Component ──
export default function WebsiteServices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState('landing');
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [questionnaire, setQuestionnaire] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const autosaveTimer = useRef(null);

  // Load jobs on mount
  useEffect(() => {
    loadJobs();
  }, []);

  // Handle checkout redirects (both build and revision)
  useEffect(() => {
    const jobId = searchParams.get('job_id');
    const revisionId = searchParams.get('revision_id');
    const checkout = searchParams.get('checkout');

    if (revisionId && checkout === 'success') {
      handleRevisionCheckoutSuccess(revisionId);
      setSearchParams({}, { replace: true });
    } else if (revisionId && checkout === 'cancel') {
      toast.info('Revision payment cancelled. You can try again when ready.');
      setSearchParams({}, { replace: true });
    } else if (jobId && checkout === 'success') {
      handleCheckoutSuccess(jobId);
      setSearchParams({}, { replace: true });
    } else if (jobId && checkout === 'cancel') {
      toast.info('Checkout cancelled. You can try again when ready.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const loadJobs = async () => {
    try {
      const res = await websiteServicesAPI.listJobs();
      setJobs(res.data);
    } catch {
      // Silent fail on first load
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutSuccess = async (jobId) => {
    try {
      const res = await websiteServicesAPI.confirmPayment(jobId);
      setActiveJob(res.data);
      setStep('status');
      toast.success('Payment confirmed! Building your website...');
    } catch {
      toast.error('Could not confirm payment. Please contact support.');
    }
  };

  const handleRevisionCheckoutSuccess = async (revisionId) => {
    try {
      const res = await websiteServicesAPI.revisionConfirmPayment(revisionId);
      // Find the parent job to set context
      if (res.data.job_id) {
        try {
          const jobRes = await websiteServicesAPI.getJob(res.data.job_id);
          setActiveJob(jobRes.data);
          setQuestionnaire(jobRes.data.questionnaire || {});
        } catch { /* fall through */ }
      }
      setStep('revision');
      toast.success('Payment confirmed! Your revision is being processed...');
    } catch {
      toast.error('Could not confirm revision payment. Please contact support.');
    }
  };

  const handleStart = async () => {
    try {
      const res = await websiteServicesAPI.createJob();
      setActiveJob(res.data);
      setQuestionnaire({});
      setStep('questionnaire');
    } catch {
      toast.error('Failed to start. Please try again.');
    }
  };

  const handleResumeJob = (job) => {
    setActiveJob(job);
    setQuestionnaire(job.questionnaire || {});
    if (job.status === 'completed' || job.status === 'failed' || job.status === 'building' || job.status === 'queued') {
      setStep('status');
    } else if (job.status === 'ready_for_purchase') {
      setStep('review');
    } else {
      setStep('questionnaire');
    }
  };

  const handleRequestRevision = (job) => {
    setActiveJob(job);
    setQuestionnaire(job.questionnaire || {});
    setStep('revision');
  };

  const saveQuestionnaire = async (showToast = true) => {
    if (!activeJob) return;
    setSaving(true);
    try {
      const res = await websiteServicesAPI.saveQuestionnaire(activeJob.id, { questionnaire });
      setActiveJob(res.data);
      if (showToast) toast.success('Draft saved');
    } catch {
      if (showToast) toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Autosave
  const handleQuestionnaireChange = (data) => {
    setQuestionnaire(data);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (activeJob) {
        websiteServicesAPI.saveQuestionnaire(activeJob.id, { questionnaire: data }).catch(() => {});
      }
    }, 5000);
  };

  const handleSelectPackage = async (packageType) => {
    if (!activeJob) return;
    setPurchasing(true);
    try {
      // Save questionnaire first
      await websiteServicesAPI.saveQuestionnaire(activeJob.id, { questionnaire });
      await websiteServicesAPI.selectPackage(activeJob.id, { package_type: packageType });

      // Try Stripe checkout
      try {
        const checkoutRes = await websiteServicesAPI.createCheckout(activeJob.id);
        // Handle comped tenant — build starts immediately, no Stripe redirect
        if (checkoutRes.data.comped) {
          setActiveJob(checkoutRes.data.job);
          setStep('status');
          toast.success('Build started! Your account has billing bypass enabled.');
          return;
        }
        window.location.href = checkoutRes.data.checkout_url;
      } catch (stripeErr) {
        // Stripe not configured — offer demo build
        if (stripeErr.response?.status === 503 || stripeErr.response?.status === 502) {
          toast.info('Stripe not configured. Starting demo build instead.');
          const res = await websiteServicesAPI.demoBuild(activeJob.id);
          setActiveJob(res.data);
          setStep('status');
        } else {
          throw stripeErr;
        }
      }
    } catch (err) {
      toast.error('Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const pollJob = useCallback(async () => {
    if (!activeJob) return;
    try {
      const res = await websiteServicesAPI.getJob(activeJob.id);
      setActiveJob(res.data);
      if (res.data.status === 'completed') {
        toast.success('Your website build is complete!');
        loadJobs();
      } else if (res.data.status === 'failed') {
        toast.error('Website build failed.');
        loadJobs();
      }
    } catch {
      // silent
    }
  }, [activeJob?.id]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20" data-testid="website-loading">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6" data-testid="website-services-page">
        {/* Step indicator for non-landing steps */}
        {step !== 'landing' && step !== 'status' && step !== 'revision' && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-5 flex-wrap" data-testid="step-indicator">
            {['Questionnaire', 'Review', 'Package'].map((s, i) => {
              const stepMap = ['questionnaire', 'review', 'package'];
              const current = stepMap.indexOf(step);
              return (
                <span key={s} className="flex items-center gap-1">
                  {i > 0 && <span className="mx-1">/</span>}
                  <span className={i <= current ? 'text-foreground font-medium' : ''}>{s}</span>
                </span>
              );
            })}
          </div>
        )}

        {step === 'landing' && (
          <LandingSection
            onStart={handleStart}
            existingJobs={jobs}
            onResumeJob={handleResumeJob}
            onRequestRevision={handleRequestRevision}
          />
        )}

        {step === 'questionnaire' && (
          <QuestionnaireSection
            data={questionnaire}
            onChange={handleQuestionnaireChange}
            onBack={() => setStep('landing')}
            onNext={async () => { await saveQuestionnaire(false); setStep('review'); }}
            onSave={() => saveQuestionnaire(true)}
            saving={saving}
          />
        )}

        {step === 'review' && (
          <ReviewSection
            data={questionnaire}
            onBack={() => setStep('questionnaire')}
            onNext={() => setStep('package')}
          />
        )}

        {step === 'package' && (
          <PackageSection
            onBack={() => setStep('review')}
            onSelectPackage={handleSelectPackage}
            loading={purchasing}
          />
        )}

        {step === 'status' && (
          <StatusSection
            job={activeJob}
            onBack={() => { setActiveJob(null); setStep('landing'); loadJobs(); }}
            onPoll={pollJob}
            onRequestRevision={handleRequestRevision}
          />
        )}

        {step === 'revision' && (
          <RevisionSection
            job={activeJob}
            onBack={() => { setActiveJob(null); setStep('landing'); loadJobs(); }}
            onComplete={() => { setActiveJob(null); setStep('landing'); loadJobs(); }}
          />
        )}
      </div>
    </Layout>
  );
}
